import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { setCurrentClient } from '../../redux/client/client.actions';
import {
    sendMessage,
    finishRoom,
} from '../../redux/room/room.actions';
import { getUsers, logOut, selectUserStatus, selectUsers, setUsers, updateUserPresence, setUserStatus } from '../../redux/user/user.actions';

import { ExpandLess, ExpandMore, FilterList, HeadsetMic, Search } from '@mui/icons-material';
import Icon from '@material-ui/core/Icon'
import './indicator.css';
import { Grid, Typography, IconButton, Button, Menu, MenuItem, FormControl, InputLabel, Select, Box, Card, CardContent, Paper, Avatar, Collapse, Divider, TextField, InputAdornment, Tooltip } from '@mui/material';
import TableInfo from '../../components/TableInfo';
import { ThemeContext } from '../../components/ThemeContext/ThemeContext';
import { setLoading, setNotLoading } from '../../redux/loading/loading.actions';
import { getRoomsIndicators, getTicketsIndicators, getTicketsUserIndicators, getTeamIndicators, selectRoomIndicators, selectTicketIndicators, selectTicketUserIndicators, selectTeamIndicators } from '../../redux/indicator/indicator.actions';
import TicketAccordion from '../../components/TicketAccordion';
import { useWebSocket } from '../../components/WebSocketContext/WebSocketContext';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { formatAttendanceDuration } from '../../utils/formatAttendanceDuration';
import { hasAdminProfile } from '../../utils/profileRole';
import { gsap, ScrollTrigger } from '../../services/gsap';
import { revelarAoRolar, pararAnimacoes } from '../../utils/animacoesDaPagina';
import NumeroAnimado from '../../components/NumeroAnimado';
import FundoAnimado from '../../components/FundoAnimado';

const PRESENCE_PAUSE_PERMISSION = '#pausar-status-online-operadores';

const IndicatorPage = props => {
    const [filteredOperators, setFilteredOperators] = useState([])
    const { theme } = useContext(ThemeContext);
    const history = useHistory();

    /*
     * As seções que aparecem ao rolar.
     *
     * Um `ref` por seção, guardado por índice: o GSAP precisa dos elementos,
     * e colecioná-los aqui evita espalhar `useRef` por toda a árvore. A
     * ausência de qualquer um deles é tratada em `revelarAoRolar` -- seção
     * que não montou simplesmente não anima.
     */
    const secoes = useRef([]);
    const areaRolavel = useRef(null);
    const guardarSecao = useCallback((indice) => (elemento) => {
        secoes.current[indice] = elemento;
    }, []);

    /*
     * A entrada das seções ao rolar.
     *
     * Roda uma vez, depois da primeira pintura. Não depende dos dados: as
     * seções existem desde o começo (vazias ou carregando), e esperar o dado
     * para animar deixaria a tela estática justamente no momento em que ela
     * mais muda.
     */
    useEffect(() => {
        const animacoes = revelarAoRolar(secoes.current, {
            gsap,
            ScrollTrigger,
            scroller: areaRolavel.current,
        });

        return () => pararAnimacoes(animacoes);
    }, []);

    const openTicketsFiltered = useCallback(
        (scope, sub) => {
            const search = new URLSearchParams({ indScope: scope, indSub: sub }).toString();
            history.push({ pathname: '/ticket', search: `?${search}` });
        },
        [history]
    );

     const [anchorElStatus, setAnchorElStatus] = useState(null);
     const [selectedTimeFrame, setSelectedTimeFrame] = useState('day');
     const [selectedStatus, setSelectedStatus] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');

     const {
         getUsers,
         setCurrentClient,
         getTicketsIndicators,
         getTicketsUserIndicators,
         getTeamIndicators,
         setUsers,
         getRoomsIndicators,
         currentUser,
         permissions,
         updateUserPresence: pushPresenceRemote,
         setUserStatus: setPresenceLocal,
     } = props;

     const { updateUserStatus: pushPresenceWs } = useWebSocket();

     const canChangeOwnPresence = useMemo(() => {
         const list = Array.isArray(permissions) ? permissions : [];
         return (
             list.includes(PRESENCE_PAUSE_PERMISSION) ||
             hasAdminProfile(currentUser) ||
             Boolean(currentUser?.is_super_admin)
         );
     }, [permissions, currentUser?.profile_id, currentUser?.is_super_admin]);

     const isPresenceAdmin = useMemo(
         () => hasAdminProfile(currentUser) || Boolean(currentUser?.is_super_admin),
         [currentUser?.profile_id, currentUser?.is_super_admin],
     );

     const roomIndicators = useSelector(selectRoomIndicators);
     const ticketIndicators = useSelector(selectTicketIndicators);
     const ticketUserIndicators = useSelector(selectTicketUserIndicators);
     const teamIndicators = useSelector(selectTeamIndicators);
     const users = useSelector(selectUsers);

    const handleClickStatus = (event) => {
        setAnchorElStatus(event.currentTarget);
    };

    const handleCloseStatus = () => {
        setAnchorElStatus(null);
    };

    const handleSelect = (value) => {
        setSelectedTimeFrame(value);
    };

    const handleSelectStatus = (value) => {
        setSelectedStatus(value);
        handleCloseStatus();
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };


    useEffect(() => {
        getRoomsIndicators(selectedTimeFrame, true);
    }, [selectedTimeFrame, getRoomsIndicators]);

    const getIndicators = useCallback(async () => {
        getUsers();
    }, [getUsers]);

    useEffect(() => {
        getIndicators();

        return () => {
            setCurrentClient({});
        };
    }, [getIndicators, setCurrentClient]);

     useEffect(() => {
         if (!currentUser) return;
         getTicketsIndicators();
         getTicketsUserIndicators(currentUser.id);
         if (currentUser.service_group_id) {
             getTeamIndicators(currentUser.service_group_id);
         }
     // eslint-disable-next-line react-hooks/exhaustive-deps
     }, [selectedTimeFrame, currentUser?.id, currentUser?.service_group_id, getTicketsIndicators, getTicketsUserIndicators, getTeamIndicators]);

    const [expandedCards, setExpandedCards] = useState({});

    const toggleCardExpansion = (operatorId) => {
        setExpandedCards(prevState => ({
            ...prevState,
            [operatorId]: !prevState[operatorId],
        }));
    };

    const handlePresenceSwitch = useCallback((operator, wantOnline) => {
        const next = wantOnline ? 1 : 3;
        const selfId = Number(currentUser?.id);
        if (Number(operator.id) === selfId) {
            setPresenceLocal({ userId: operator.id, status: next });
        }
        pushPresenceRemote(operator.id, next);
        pushPresenceWs({ userId: operator.id, status: next });
    }, [currentUser?.id, pushPresenceRemote, pushPresenceWs, setPresenceLocal]);

    const OperatorCard = ({ operator }) => {
        const isExpanded = expandedCards[operator.id] || false;
        const department = operator?.service_group?.description || 'Departamento não informado';
        const isSelf = Number(operator.id) === Number(currentUser?.id);
        const online = operator.status === 1 || operator.status === 2;
        const accountInactive = operator.status === 4;
        const canToggleHere =
            !accountInactive &&
            ((isSelf && canChangeOwnPresence) || (!isSelf && isPresenceAdmin));

        const presenceLabel = accountInactive ? 'Conta inativa' : online ? 'Ativo' : 'Inativo';
        const dotColor = accountInactive
            ? theme.palette.text.disabled
            : online
                ? theme.palette.success.main
                : theme.palette.error.main;

        const cardRadius = Number(theme.shape.borderRadius) || 10;
        const pillRadius = Math.max(cardRadius - 4, 4);

        const pillSx = {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 0.75,
            py: 0.25,
            borderRadius: `${pillRadius}px`,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'action.hover',
            maxWidth: '100%',
        };

        return (
            <Paper
                elevation={0}
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: `${cardRadius}px`,
                    bgcolor: 'background.paper',
                    overflow: 'hidden',
                }}
            >
                <Box sx={{ px: 1.25, py: 1.125, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, flex: 1 }}>
                            <Avatar
                                src={operator.picture}
                                alt={`Foto de ${operator.name}`}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    bgcolor: theme.palette.primary.main,
                                    flexShrink: 0,
                                    fontSize: '0.875rem',
                                }}
                            >
                                {!operator.picture && (
                                    <Icon style={{ fontSize: 18 }}>person</Icon>
                                )}
                            </Avatar>
                            <Box sx={{ minWidth: 0, py: 0.125 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.25 }} noWrap title={operator.name}>
                                    {operator.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap title={department} sx={{ display: 'block', lineHeight: 1.2, mt: 0.125 }}>
                                    {department}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                    {canToggleHere ? (
                                        <Tooltip title={online ? 'Marcar como inativo' : 'Marcar como ativo'}>
                                            <Box
                                                component="button"
                                                type="button"
                                                aria-pressed={online}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handlePresenceSwitch(operator, !online);
                                                    }
                                                }}
                                                onClick={() => handlePresenceSwitch(operator, !online)}
                                                sx={{
                                                    ...pillSx,
                                                    cursor: 'pointer',
                                                    border: '1px solid',
                                                    borderColor: online
                                                        ? alpha(theme.palette.success.main, 0.35)
                                                        : alpha(theme.palette.error.main, 0.35),
                                                    bgcolor: online
                                                        ? alpha(theme.palette.success.main, 0.1)
                                                        : alpha(theme.palette.error.main, 0.08),
                                                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                                                    font: 'inherit',
                                                    color: 'inherit',
                                                    textAlign: 'left',
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        bgcolor: online
                                                            ? alpha(theme.palette.success.main, 0.16)
                                                            : alpha(theme.palette.error.main, 0.14),
                                                    },
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        width: 5,
                                                        height: 5,
                                                        borderRadius: '50%',
                                                        flexShrink: 0,
                                                        bgcolor: dotColor,
                                                    }}
                                                />
                                                <Typography component="span" variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1 }}>
                                                    {presenceLabel}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    ) : (
                                        <Box sx={pillSx}>
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 5,
                                                    height: 5,
                                                    borderRadius: '50%',
                                                    flexShrink: 0,
                                                    bgcolor: dotColor,
                                                }}
                                            />
                                            <Typography component="span" variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1 }}>
                                                {presenceLabel}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        <IconButton
                            size="small"
                            onClick={() => toggleCardExpansion(operator.id)}
                            aria-label={isExpanded ? 'Ocultar detalhes do operador' : 'Expandir detalhes do operador'}
                            sx={{ flexShrink: 0, p: 0.5, alignSelf: 'flex-start', mt: 0.125 }}
                        >
                            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                        </IconButton>
                    </Box>
                    <Collapse
                        in={isExpanded}
                        timeout="auto"
                        unmountOnExit
                        sx={{
                            width: '100%',
                            '& .MuiCollapse-wrapper': { width: '100%' },
                            '& .MuiCollapse-wrapperInner': { width: '100%' },
                        }}
                    >
                        <Box sx={{ pt: 1, width: '100%', boxSizing: 'border-box' }}>
                            <Divider sx={{ borderColor: 'divider' }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pt: 1 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', lineHeight: 1.3 }}>
                                        Departamento
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', lineHeight: 1.35, mt: 0.25 }}>
                                        {department}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', lineHeight: 1.3 }}>
                                        Total de atendimentos
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 500, display: 'block', lineHeight: 1.35, mt: 0.25 }}>
                                        {operator.total ?? 0}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Collapse>
                </Box>
            </Paper>
        );
    };

    const CustomToolbar = ({ selectedTimeFrame, handleSelect }) => {
        return (
            <Grid container justifyContent="flex-end" spacing={1} sx={{ backgroundColor: "transparent", padding: "0.25rem" }}>
                <Grid item sx={{ backgroundColor: "transparent" }}>
                    <FormControl variant="outlined" size="small">
                        <InputLabel>Período</InputLabel>
                        <Select
                            label="Período"
                            value={selectedTimeFrame}
                            onChange={(event) => handleSelect(event.target.value)}
                        >
                            <MenuItem value="day">Hoje</MenuItem>
                            <MenuItem value="week">Essa semana</MenuItem>
                            <MenuItem value="month">Este mês</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        );
    };

     const ticketShortcutRowSx = {
         cursor: 'pointer',
         borderRadius: 1,
         px: 0.5,
         mx: -0.5,
         py: 0.25,
         transition: 'background-color 0.15s ease',
         '&:hover': {
             backgroundColor: theme.palette.action.hover,
         },
     };

     const TicketCard = ({ title, data, scope }) => {
         const pending = data.pending || 0;
         const newTickets = data.new || 0;
         const inProgress = data.inProgress || 0;
         const stalled = data.stalled || 0;
         const openedToday = data.openedToday || 0;
         const hasAttendanceMetrics = data.attendanceSecondsToday != null
             || data.attendanceSecondsMonth != null
             || data.attendanceActiveTickets != null;

         return (
             <Card
                 sx={{
                     height: '100%',
                     display: 'flex',
                     flexDirection: 'column',
                     /*
                      * Em px, de proposito. O numero puro no `borderRadius` do
                      * MUI e MULTIPLICADOR de `theme.shape.borderRadius`, e a
                      * base aqui e 10, nao os 4 do padrao da biblioteca: um
                      * `borderRadius: 3` escrito distraidamente virou 30px --
                      * canto de pilula num cartao, dentro de um painel de
                      * 10px. 14px e um degrau acima do painel (o `--radius-lg`
                      * dos tokens do tema), que e o suficiente para o cartao
                      * se destacar sem brigar com a moldura.
                      */
                     borderRadius: '14px',
                     border: `1px solid ${theme.palette.divider}`,
                     // A sombra some no tema escuro, onde ela vira uma mancha
                     // suja em vez de profundidade; lá quem separa é a borda.
                     boxShadow: theme.palette.mode === 'dark'
                         ? 'none'
                         : `0 1px 2px ${alpha('#000', 0.04)}, 0 8px 24px ${alpha('#000', 0.06)}`,
                     transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
                     '&:hover': {
                         borderColor: alpha(theme.palette.primary.main, 0.45),
                         boxShadow: theme.palette.mode === 'dark'
                             ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.25)}`
                             : `0 2px 4px ${alpha('#000', 0.05)}, 0 14px 32px ${alpha('#000', 0.1)}`,
                     },
                     // Quem pede movimento reduzido não ganha o deslocamento
                     // no hover; a mudança de borda continua, porque ela é
                     // informação (dá para saber onde o ponteiro está).
                     '@media (prefers-reduced-motion: no-preference)': {
                         '&:hover': { transform: 'translateY(-2px)' },
                     },
                 }}
             >
                 <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2.5 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                         <Box
                             sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 justifyContent: 'center',
                                 width: 36,
                                 height: 36,
                                 // Mesma armadilha: `2` seriam 20px numa caixa
                                 // de 36, ou seja quase um circulo.
                                 borderRadius: '10px',
                                 backgroundColor: alpha(theme.palette.primary.main, 0.12),
                             }}
                         >
                             <HeadsetMic sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                         </Box>
                         <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
                             {title}
                         </Typography>
                     </Box>
                     <Box sx={{
                         width: '100%',
                         height: 3,
                         borderRadius: '999px',
                         // A régua de 2px virou um degradê que termina
                         // transparente: separa sem cortar o cartão em dois.
                         background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0)} 100%)`,
                         mb: 2
                     }} />

                     <Box sx={{ mb: 2 }}>
                         <Box
                             role="button"
                             tabIndex={0}
                             onKeyDown={(e) => {
                                 if (e.key === 'Enter' || e.key === ' ') {
                                     e.preventDefault();
                                     openTicketsFiltered(scope, 'pending');
                                 }
                             }}
                             onClick={() => openTicketsFiltered(scope, 'pending')}
                             sx={ticketShortcutRowSx}
                         >
                             <Typography
                                 variant="h3"
                                 sx={{
                                     fontWeight: 800,
                                     color: theme.palette.text.primary,
                                     mb: 1,
                                     fontSize: { xs: '2.25rem', md: '2.75rem' },
                                     lineHeight: 1.05,
                                     letterSpacing: '-0.03em',
                                     /*
                                      * A sombra de texto saiu: ela borrava os
                                      * dígitos no tema escuro, que é onde a
                                      * maioria trabalha. O destaque agora vem
                                      * do peso e do tamanho.
                                      */
                                 }}
                             >
                                 <NumeroAnimado valor={pending} gsap={gsap} />
                                 {' '}
                                 <Box
                                     component="span"
                                     sx={{
                                         fontSize: '0.95rem',
                                         fontWeight: 600,
                                         color: theme.palette.text.secondary,
                                         letterSpacing: 0,
                                     }}
                                 >
                                     pendentes
                                 </Box>
                             </Typography>
                         </Box>
                         <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                             <Box
                                 role="button"
                                 tabIndex={0}
                                 onKeyDown={(e) => {
                                     if (e.key === 'Enter' || e.key === ' ') {
                                         e.preventDefault();
                                         openTicketsFiltered(scope, 'new');
                                     }
                                 }}
                                 onClick={() => openTicketsFiltered(scope, 'new')}
                                 sx={ticketShortcutRowSx}
                             >
                                 <Typography variant="body2">{newTickets} novos</Typography>
                             </Box>
                             <Box
                                 role="button"
                                 tabIndex={0}
                                 onKeyDown={(e) => {
                                     if (e.key === 'Enter' || e.key === ' ') {
                                         e.preventDefault();
                                         openTicketsFiltered(scope, 'inProgress');
                                     }
                                 }}
                                 onClick={() => openTicketsFiltered(scope, 'inProgress')}
                                 sx={ticketShortcutRowSx}
                             >
                                 <Typography variant="body2">{inProgress} em atendimento</Typography>
                             </Box>
                             <Box
                                 role="button"
                                 tabIndex={0}
                                 onKeyDown={(e) => {
                                     if (e.key === 'Enter' || e.key === ' ') {
                                         e.preventDefault();
                                         openTicketsFiltered(scope, 'stalled');
                                     }
                                 }}
                                 onClick={() => openTicketsFiltered(scope, 'stalled')}
                                 sx={ticketShortcutRowSx}
                             >
                                 <Typography variant="body2">{stalled} parados</Typography>
                             </Box>
                         </Box>
                     </Box>

                     {hasAttendanceMetrics && (
                         <Box
                             sx={{
                                 mt: 1.5,
                                 p: 1.5,
                                 borderRadius: 1,
                                 border: `1px solid ${theme.palette.divider}`,
                                 bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06),
                             }}
                         >
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                                 <TimerOutlinedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                 <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                     Tempo em tickets
                                 </Typography>
                             </Box>
                             {data.attendanceSecondsToday != null && (
                                 <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                     Hoje:{' '}
                                     <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                                         {formatAttendanceDuration(data.attendanceSecondsToday)}
                                     </Box>
                                 </Typography>
                             )}
                             {data.attendanceSecondsMonth != null && (
                                 <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                                     Este mês:{' '}
                                     <Box component="span" sx={{ fontWeight: 700, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                                         {formatAttendanceDuration(data.attendanceSecondsMonth)}
                                     </Box>
                                 </Typography>
                             )}
                             {data.attendanceActiveTickets != null && data.attendanceActiveTickets > 0 && (
                                 <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                                     {data.attendanceActiveTickets} ticket(s) com atendimento ativo agora
                                 </Typography>
                             )}
                             {permissions?.includes('/tempo-tickets') && (
                             <Button
                                 size="small"
                                 variant="text"
                                 sx={{ mt: 1, p: 0, textTransform: 'none', fontWeight: 600 }}
                                 onClick={() => history.push('/tempo-tickets')}
                             >
                                 Abrir relatório completo →
                             </Button>
                             )}
                         </Box>
                     )}

                     <Box
                         role="button"
                         tabIndex={0}
                         onKeyDown={(e) => {
                             if (e.key === 'Enter' || e.key === ' ') {
                                 e.preventDefault();
                                 openTicketsFiltered(scope, 'openedToday');
                             }
                         }}
                         onClick={() => openTicketsFiltered(scope, 'openedToday')}
                         sx={{
                             mt: 'auto',
                             p: 2,
                             backgroundColor: theme.palette.background.paper,
                             borderRadius: 1,
                             border: `1px solid ${theme.palette.divider}`,
                             cursor: 'pointer',
                             transition: 'background-color 0.15s ease, border-color 0.15s ease',
                             '&:hover': {
                                 backgroundColor: theme.palette.action.hover,
                                 borderColor: theme.palette.primary.main,
                             },
                         }}
                     >
                         <Typography
                             variant="h4"
                             sx={{
                                 fontWeight: 'bold',
                                 color: theme.palette.text.secondary,
                                 textAlign: 'center',
                                 mb: 0.5
                             }}
                         >
                             {openedToday}
                         </Typography>
                         <Typography
                             variant="body2"
                             sx={{
                                 textAlign: 'center',
                                 color: theme.palette.text.secondary,
                                 fontWeight: 'medium'
                             }}
                         >
                             abertos hoje
                         </Typography>
                     </Box>
                 </CardContent>
             </Card>
         );
     };

    const columnsIndicator = [
        { field: 'id', headerName: 'ID', hide: true },
        { field: 'indicator', headerName: 'Indicador', flex: 1, headerClassName: 'table-header-operators text-compact', sortable: false },
        {
            field: 'value',
            headerName: 'Total',
            flex: 1,
            headerClassName: 'table-header-operators text-compact',
            sortable: false,
            renderCell: (params) => (
                <Typography
                    variant="body1"
                    sx={{
                        fontWeight: params.row.isHighlighted ? 'bold' : 'normal',
                        color: params.row.isHighlighted ? theme.palette.primary.main : 'inherit',
                        fontSize: params.row.isHighlighted ? '1.2rem' : '1rem',
                        textShadow: params.row.isHighlighted ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                    }}
                >
                    {params.value}
                </Typography>
            )
        },
    ];

    const rowsIndicator = [
        {
            id: 1, indicator: 'Total atendimentos', value: roomIndicators.totalRooms === 0
                ? '0 atendimentos' : `${roomIndicators.totalRooms} atendimentos`
        },
        {
            id: 2, indicator: 'Duração média de atendimentos', value: roomIndicators.roomsAvgTime === '00:00:00'
                ? '0 minutos' : roomIndicators.roomsAvgTime
        },
        {
            id: 3, indicator: 'Tempo médio de espera pelo atendimento', value: roomIndicators.roomsQueueAvgTime === '00:00:00'
                ? '0 minutos' : roomIndicators.roomsQueueAvgTime
        },
        { id: 4, indicator: 'Tempo total de atendimentos', value: roomIndicators.roomsTotalTime || '0 minutos' },
        {
            id: 5, indicator: 'Total de desistências', value: roomIndicators.totalCustomerDropOut === 0
                ? '0 desistências' : roomIndicators.totalCustomerDropOut
        },
    ];

    const userStatus = useSelector(selectUserStatus);


    useEffect(() => {
        if (userStatus) {
            setFilteredOperators(prevOperators => {
                const updatedOperators = prevOperators.map(operator => {
                    if (operator.id === userStatus.userId) {
                        return { ...operator, status: userStatus.status };
                    }
                    return operator;
                });
                setUsers(updatedOperators);
                return updatedOperators;
            });
        }
    }, [userStatus, setUsers]);

    useEffect(() => {
        if (users && users.length > 0) {
            const normalizedSearch = searchTerm.trim().toLowerCase();
            const filtered = users.filter(operator => {
                const matchesStatus = selectedStatus === 0 || operator.status === selectedStatus;
                const matchesSearch = normalizedSearch.length === 0
                    || operator.name?.toLowerCase().includes(normalizedSearch);

                return matchesStatus && matchesSearch;
            });

            setFilteredOperators(filtered);
        } else {
            setFilteredOperators([]);
        }
    }, [users, selectedStatus, searchTerm]);

     const myTicketsData = {
         pending: ticketUserIndicators?.pendingAssignedTickets || 0,
         new: ticketUserIndicators?.newAssignedTickets || 0,
         inProgress: ticketUserIndicators?.inProgressAssignedTickets || 0,
         stalled: ticketUserIndicators?.stalledAssignedTickets || 0,
         openedToday: ticketUserIndicators?.openedTodayByUser || 0,
         attendanceSecondsToday: ticketUserIndicators?.attendance_seconds_today ?? 0,
         attendanceSecondsMonth: ticketUserIndicators?.attendance_seconds_month ?? 0,
         attendanceActiveTickets: ticketUserIndicators?.attendance_active_tickets ?? 0,
     };

     const teamTicketsData = {
         pending: teamIndicators?.pendingTickets || 0,
         new: teamIndicators?.newTickets || 0,
         inProgress: teamIndicators?.inProgressTickets || 0,
         stalled: teamIndicators?.stalledTickets || 0,
         openedToday: teamIndicators?.openedToday || 0,
         attendanceSecondsMonth: teamIndicators?.attendance_seconds_month ?? 0,
         attendanceActiveTickets: teamIndicators?.attendance_active_tickets ?? 0,
     };

     const generalTicketsData = {
         pending: ticketIndicators?.pendingTickets || 0,
         new: ticketIndicators?.newTickets || 0,
         inProgress: ticketIndicators?.inProgressTickets || 0,
         stalled: ticketIndicators?.stalledTickets || 0,
         openedToday: ticketIndicators?.openedToday || 0,
         attendanceSecondsMonth: ticketIndicators?.attendance_seconds_month ?? 0,
         attendanceActiveTickets: ticketIndicators?.attendance_active_tickets ?? 0,
     };

    return (
        <Grid sx={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} >
            <link rel="stylesheet" href="https://fonts.googleapis.com/icon?family=Material+Icons" />
            <Box ref={areaRolavel} sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                {/*
                  * O cabeçalho, em faixa estreita.
                  *
                  * Ele começou alto, com sobretítulo e subtítulo: 160px numa
                  * área útil de 703px, quase um quarto da primeira tela gasto
                  * dizendo o nome de uma página que o menu lateral já marca
                  * como ativa e à qual se chegou clicando nela. Numa tela que
                  * fica aberta o dia todo e que já tem 1654px de conteúdo,
                  * esse é o espaço mais caro que existe.
                  *
                  * Some o sobretítulo ("Painel") e o subtítulo, que eram
                  * leitura de primeira visita; fica o nome e a faixa. A faixa
                  * continua porque é ela que ancora o começo da página -- sem
                  * nada aqui a tela recomeça direto numa tabela -- e porque é
                  * nela que vive o fundo animado.
                  *
                  * O fundo animado fica ATRÁS (zIndex 0 nele, 1 no conteúdo),
                  * não recebe clique e some inteiro para quem pede movimento
                  * reduzido -- naquele caso a biblioteca nem é baixada.
                  */}
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        px: { xs: 2, md: 4 },
                        pt: { xs: 1.5, md: 1.75 },
                        pb: { xs: 1.5, md: 1.75 },
                        mb: 1,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.16)} 0%, ${alpha(theme.palette.primary.main, 0.04)} 45%, transparent 100%)`,
                    }}
                >
                    <FundoAnimado cor={theme.palette.primary.main} opacidade={0.45} />

                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 800,
                                lineHeight: 1.2,
                                letterSpacing: '-0.015em',
                                color: theme.palette.text.primary,
                            }}
                        >
                            Indicadores
                        </Typography>
                    </Box>
                </Box>

                <Grid container sx={{ p: 2 }}>
                <Grid item xs={12} sx={{ mb: 3 }} ref={guardarSecao(0)}>
                    <TicketAccordion
                        title={'Indicadores de atendimento'}
                        defaultExpanded
                        persistKey="alodesk:indicatorAccordion:attendance"
                    >
                        <TableInfo
                            rows={rowsIndicator}
                            columns={columnsIndicator}
                            color={theme.palette.background.default}
                            isBorder={true}
                            height={7}
                            borderRadius={'8px'}
                            marginBottom={0.5}
                            CustomToolbar={(props) => <CustomToolbar {...props} selectedTimeFrame={selectedTimeFrame} handleSelect={handleSelect} />}
                        />
                    </TicketAccordion>
                </Grid>

                 <Grid container spacing={2}>
                     <Grid item xs={12} md={4} ref={guardarSecao(1)}>
                         <TicketAccordion title={'Meus tickets'} defaultExpanded persistKey="alodesk:indicatorAccordion:myTickets">
                             <TicketCard
                                 title="Meus tickets"
                                 data={myTicketsData}
                                 scope="my"
                             />
                         </TicketAccordion>
                     </Grid>
                     <Grid item xs={12} md={4} ref={guardarSecao(2)}>
                         <TicketAccordion title={'Minha Equipe'} defaultExpanded persistKey="alodesk:indicatorAccordion:team">
                             <TicketCard
                                 title="Minha Equipe"
                                 data={teamTicketsData}
                                 scope="team"
                             />
                         </TicketAccordion>
                     </Grid>
                     <Grid item xs={12} md={4} ref={guardarSecao(3)}>
                         <TicketAccordion title={'Tickets Gerais'} defaultExpanded persistKey="alodesk:indicatorAccordion:generalTickets">
                             <TicketCard
                                 title="Tickets Gerais"
                                 data={generalTicketsData}
                                 scope="all"
                             />
                         </TicketAccordion>
                     </Grid>
                 </Grid>

                <Grid item xs={12} sx={{ mt: 3 }} ref={guardarSecao(4)}>
                    <TicketAccordion title={'Operadores'} defaultExpanded persistKey="alodesk:indicatorAccordion:operators">
                        <Box
                            sx={{
                                backgroundColor: 'transparent',
                                borderRadius: `${Number(theme.shape.borderRadius) || 10}px`,
                                p: { xs: 1, md: 1.25 },
                                border: `1px solid ${theme.palette.divider}`,
                                overflow: 'hidden',
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between', mb: 1.25 }}>
                                <TextField
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    size="small"
                                    variant="outlined"
                                    placeholder="Buscar por nome"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ width: { xs: '100%', sm: 280 } }}
                                />
                                <IconButton size="small" onClick={handleClickStatus}>
                                    <FilterList fontSize="small" />
                                </IconButton>
                                <Menu
                                    anchorEl={anchorElStatus}
                                    open={Boolean(anchorElStatus)}
                                    onClose={handleCloseStatus}
                                >
                                    <MenuItem onClick={() => handleSelectStatus(1)}>Ativo</MenuItem>
                                    <MenuItem onClick={() => handleSelectStatus(3)}>Inativo</MenuItem>
                                    <MenuItem onClick={() => handleSelectStatus(0)}>Todos</MenuItem>
                                </Menu>
                            </Box>
                            {filteredOperators.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                                    Nenhum operador encontrado para o filtro selecionado.
                                </Typography>
                            ) : (
                                <Grid container spacing={1.25} sx={{ mt: 0.25 }}>
                                    {filteredOperators.map(operator => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={operator.id}>
                                            <OperatorCard operator={operator} />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Box>
                    </TicketAccordion>
                </Grid>
            </Grid>
            </Box>
        </Grid>
    );
};

const mapStateToProps = state => {
    const { rooms, clients, user } = state;
    const { inProgressRooms } = rooms;
    const { currentClient } = clients;
    const { currentUser, permissions } = user;
    return { inProgressRooms, currentClient, user, currentUser, permissions };
};

const mapDispatchToProps = dispatch => ({
     sendMessage: data => dispatch(sendMessage(data)),
     setCurrentClient: data => dispatch(setCurrentClient(data)),
     finishRoom: (roomId, userId) => dispatch(finishRoom(roomId, userId)),
     getUsers: () => dispatch(getUsers()),
     logOut: () => dispatch(logOut()),
     setLoading: () => dispatch(setLoading()),
     setNotLoading: () => dispatch(setNotLoading()),
     getTicketsIndicators: () => dispatch(getTicketsIndicators()),
     getTicketsUserIndicators: (userId) => dispatch(getTicketsUserIndicators(userId)),
     getTeamIndicators: (serviceGroupId) => dispatch(getTeamIndicators(serviceGroupId)),
     setUsers: (users) => dispatch(setUsers(users)),
     getRoomsIndicators: (dataRange, formatDate) => dispatch(getRoomsIndicators(dataRange, formatDate)),
     updateUserPresence: (userId, status) => dispatch(updateUserPresence(userId, status)),
     setUserStatus: (payload) => dispatch(setUserStatus(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(IndicatorPage);